import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { COMPANY } from "../mock";
import { useEstimate } from "./EstimateContext";

const MENU = [
  {
    label: "About",
    dropdown: [
      { label: "Our Story", to: "/about" },
      { label: "Reviews", to: "/about#reviews" },
    ],
  },
  {
    label: "Residential",
    dropdown: [
      { label: "Kitchen Renovations", to: "/services/kitchen" },
      { label: "Bathroom Renovations", to: "/services/bathroom" },
      { label: "Basement Renovations", to: "/services/basement" },
      { label: "Full Home Remodeling", to: "/services/full-home" },
      { label: "Legal Suites", to: "/services/legal-suites" },
      { label: "Design Studio", to: "/services/design-studio" },
    ],
  },
  { label: "Commercial", to: "/commercial" },
  { label: "Projects", to: "/projects" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const [mobileDrop, setMobileDrop] = useState(null);
  const { openEstimate } = useEstimate();
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDrop(null);
    setMobileDrop(null);
  }, [location]);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || mobileOpen;

  const enter = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDrop(label);
  };
  const leave = () => {
    closeTimer.current = setTimeout(() => setOpenDrop(null), 140);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(15,39,64,0.08)] py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container-x flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <img src="/assets/logo.png" alt="Classic Interior Renovations logo" className="w-11 h-11 object-contain transition-transform group-hover:scale-105" />
          <div className="leading-tight">
            <div className={`font-display font-bold text-[15px] sm:text-[17px] tracking-tight ${solid ? "text-[#0f2740]" : "text-white"}`}>CLASSIC INTERIOR</div>
            <div className={`text-[10px] sm:text-[11px] tracking-[0.22em] ${solid ? "text-[#2f9fe6]" : "text-[#bfe0f5]"}`}>RENOVATIONS LTD</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {MENU.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="relative" onMouseEnter={() => enter(item.label)} onMouseLeave={leave}>
                <button className={`link-underline flex items-center gap-1 text-[15px] font-medium transition-colors ${solid ? "text-[#33414f] hover:text-[#2f9fe6]" : "text-white/90 hover:text-white"}`}>
                  {item.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDrop === item.label ? "rotate-180" : ""}`} />
                </button>
                <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-all duration-200 ${openDrop === item.label ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-2"}`}>
                  <div className="bg-[#0f2740] rounded-2xl shadow-2xl py-3 min-w-[260px] overflow-hidden">
                    {item.dropdown.map((d) => (
                      <Link key={d.label} to={d.to} className="block px-6 py-3 text-white/85 hover:text-white hover:bg-white/5 hover:pl-7 transition-all text-[15px]">
                        {d.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={item.label} to={item.to} className={`link-underline text-[15px] font-medium transition-colors ${solid ? "text-[#33414f] hover:text-[#2f9fe6]" : "text-white/90 hover:text-white"}`}>
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a href={COMPANY.phoneHref} className={`flex items-center gap-2 text-sm font-medium ${solid ? "text-[#0f2740]" : "text-white"}`}>
            <Phone className="w-4 h-4 text-[#2f9fe6]" /> {COMPANY.phone}
          </a>
          <button onClick={openEstimate} className="px-6 py-2.5 rounded-full bg-[#2f9fe6] text-white font-semibold text-sm hover:bg-[#1f82c7] transition-all hover:shadow-lg hover:shadow-[#2f9fe6]/30">
            Get an Estimate
          </button>
        </div>

        <button className={`lg:hidden p-2 ${solid ? "text-[#0f2740]" : "text-white"}`} onClick={() => setMobileOpen((v) => !v)} aria-label="Menu">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? "max-h-[640px]" : "max-h-0"}`}>
        <div className="container-x py-4 flex flex-col">
          {MENU.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="border-b border-[#eef1f4]">
                <button onClick={() => setMobileDrop(mobileDrop === item.label ? null : item.label)} className="w-full flex items-center justify-between py-3 text-[#33414f] font-medium">
                  {item.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileDrop === item.label ? "rotate-180" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${mobileDrop === item.label ? "max-h-72 pb-2" : "max-h-0"}`}>
                  {item.dropdown.map((d) => (
                    <Link key={d.label} to={d.to} className="block py-2.5 pl-4 text-[#6b7683] hover:text-[#2f9fe6]">{d.label}</Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link key={item.label} to={item.to} className="py-3 border-b border-[#eef1f4] text-[#33414f] font-medium">{item.label}</Link>
            )
          )}
          <a href={COMPANY.phoneHref} className="py-3 flex items-center gap-2 text-[#0f2740] font-medium">
            <Phone className="w-4 h-4 text-[#2f9fe6]" /> {COMPANY.phone}
          </a>
          <button onClick={openEstimate} className="mt-2 px-6 py-3 rounded-full bg-[#2f9fe6] text-white font-semibold">Get an Estimate</button>
        </div>
      </div>
    </header>
  );
}
